// Factory for rendering workflow form fields

import {
  CommentField,
  type CommentFieldProps,
} from "@/shared/components/comment-field/comment-field";
import { Typography } from "@spz/ui/components/typography";
import { useTranslation } from "react-i18next";
import type {
  ConclusionSelectField as ConclusionSelectFieldType,
  Field,
  FieldValue,
} from "../../../shared/types/workflow/workflow-form.types";
import {
  transformFieldValueToCommentValue,
  transformFieldValueToSelectValue,
} from "../utils";
import { ConclusionSelectField } from "./fields/conclusion-select-field";
import { SelectField } from "./fields/select-field";

import type { FieldValidationError } from "../../../shared/types/workflow/workflow-form.types";

export interface WorkflowFormFieldProps {
  companyId: string;
  field: Field;
  value: FieldValue;
  error?: FieldValidationError;
  onChange: (value: FieldValue) => void;
  onBlur: () => void;
}

export function WorkflowFormField({
  companyId,
  field,
  value,
  error,
  onChange,
  onBlur,
}: WorkflowFormFieldProps) {
  const { t } = useTranslation();

  const commonProps = {
    id: field.name,
    name: field.name,
    label: t(field.label_key) || field.name,
    placeholder: t(field.placeholder_key ?? ""),
    onBlur,
  };

  switch (field.type) {
    case "select": {
      const selectValue = transformFieldValueToSelectValue(value);
      const options = field.options.map((opt) => ({
        ...opt,
        label: t(opt.label_key),
      }));

      return (
        <div className="mb-4">
          <SelectField
            {...commonProps}
            options={options}
            value={selectValue}
            multi={!!field.multiple}
            error={error}
            onSelect={(selected) => {
              // Only pass string/number or array of string/number as FieldValue
              if (field.multiple) {
                onChange(selected as unknown as FieldValue);
              } else {
                onChange((selected[0] ?? null) as FieldValue);
              }
            }}
          />
          {error && (
            <Typography
              className="mt-1 text-red-500"
              trans={error.translationKey}
              transParams={error.params}
            />
          )}
        </div>
      );
    }
    case "conclusion_select": {
      const conclusionSelectField: ConclusionSelectFieldType = field;

      return (
        <ConclusionSelectField
          {...commonProps}
          field={conclusionSelectField}
          value={value}
          error={error}
          onChange={onChange}
        />
      );
    }
    case "comment": {
      const commentField = field;
      const initialValues: CommentFieldProps["initialValues"] =
        transformFieldValueToCommentValue(value);
      const allowedFileTypes =
        commentField.validation?.attachment?.accepted_formats;

      return (
        <div className="mb-4">
          <CommentField
            {...commonProps}
            companyId={companyId}
            initialValues={initialValues}
            allowedFileTypes={allowedFileTypes}
            onChange={onChange}
          />
          {error && (
            <Typography
              className="mt-1 text-red-500"
              trans={error.translationKey}
              transParams={error.params}
            />
          )}
        </div>
      );
    }
    default:
      return null;
  }
}
